import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckboxControl, DialogControl, FieldControl } from 'spark-core-dx/components';
import { useDashboardDomainContext } from '../../DashboardDomainProvider';
import { useCorePermissions, useGenericManager, useValidationErrors } from 'spark-core-dx/hooks';
import { SystemTypes } from 'spark-core-dx/services';
import { ItemServiceFactory } from '../../../../services/ItemServiceFactory';

const createBlankDashboardDraft = () => ({
    name: "",
    description: "",
    icon: "",
    isTemplate: false,
    dataModelWorkspaceId: "",
});

const createDashboardDraftFromItem = (dashboard) => ({
    name: String(dashboard?.DraftDetails?.Name ?? dashboard?.PublishedDetails?.Name ?? "").trim(),
    description: String(dashboard?.DraftDetails?.Description ?? dashboard?.PublishedDetails?.Description ?? "").trim(),
    icon: String(dashboard?.DraftDetails?.Icon ?? dashboard?.PublishedDetails?.Icon ?? "").trim(),
    isTemplate: dashboard?.State?.IsTemplate === true,
    dataModelWorkspaceId: String(dashboard?.DataModelWorkspaceId ?? "").trim(),
});

const normalizeWorkspaceOptions = (items = []) =>
    (Array.isArray(items) ? items : [])
        .map((item) => {
            const value = String(item?._id ?? item?.UniqueId ?? item?.Value ?? "").trim();
            const text = String(item?.Name ?? item?.DisplayName ?? item?.Text ?? "").trim();

            if (!value || !text) {
                return null;
            }

            return {
                Text: text,
                Value: value,
                Sequence: typeof item?.Sequence === "number" ? item.Sequence : 0,
            };
        })
        .filter(Boolean)
        .sort((a, b) => String(a?.Text ?? "").localeCompare(String(b?.Text ?? "")));

const normalizeSelectionValue = (value) => {
    if (Array.isArray(value)) {
        return normalizeSelectionValue(value[0]);
    }

    if (value && typeof value === "object") {
        return String(value?._id ?? value?.UniqueId ?? value?.Value ?? value?.value ?? value?.Id ?? "").trim();
    }

    return String(value ?? "").trim();
};

const unwrapServiceResult = (result) => (Array.isArray(result) ? result?.[0] : result) ?? null;

const getValidationDisplayMessages = (result) =>
    Object.values(unwrapServiceResult(result)?.ValidationErrors ?? {})
        .map((entry) => String(entry?.DisplayMessage ?? entry?.Content ?? "").trim())
        .filter(Boolean);

const hasWorkspaceOption = (options, value) => {
    const normalizedValue = String(value ?? "").trim();
    if (!normalizedValue) {
        return false;
    }

    return (Array.isArray(options) ? options : []).some((option) => String(option?.Value ?? "").trim() === normalizedValue);
};

const DashboardCreateDialog = ({ isActive, closeDialog, dashboard = null, onSave = null }) => {
    const {
        iconSelectData,
        createNewDashboard,
    } = useDashboardDomainContext();

    const { canCreate } = useCorePermissions();
    const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
    const workspaceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModelWorkspace);
    const workspaceService = useMemo(
        () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.DataModelWorkspace),
        [itemServiceFactory]
    );

    const { hasAndShowValidationErrors } = useValidationErrors()

    const [dashCreationObject, setDashCreationObject] = useState(createBlankDashboardDraft())
    const [workspaceOptions, setWorkspaceOptions] = useState([]);
    const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
    const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);

    const clearDashCreationObject = () => {
        setDashCreationObject(createBlankDashboardDraft())
    }

    const [error, setError] = useState("");
    const canCreateWorkspace = canCreate("DataModelWorkspace") || canCreate("DataModel");
    const isEditMode = !!dashboard?._id;
    const linkedWorkspaceId = String(dashboard?.DataModelWorkspaceId ?? "").trim();
    const hasLinkedWorkspace = isEditMode && linkedWorkspaceId !== "";
    const hasValidLinkedWorkspace = hasLinkedWorkspace && hasWorkspaceOption(workspaceOptions, linkedWorkspaceId);
    const hasMissingLinkedWorkspace = hasLinkedWorkspace && !isLoadingWorkspaces && !hasValidLinkedWorkspace;
    const isWorkspaceLocked = hasValidLinkedWorkspace || (hasLinkedWorkspace && isLoadingWorkspaces);
    const canAutoCreateWorkspace = canCreateWorkspace || workspaceOptions.length === 0;
    const dialogTitle = isEditMode ? "Edit Dashboard" : "Design a New Dashboard";
    const dialogOkText = isEditMode ? "Save" : "Design";

    const modelWorkspaceOptions = useMemo(
        () => [
            ...(canAutoCreateWorkspace ? [{ Text: "Create New Workspace Automatically", Value: "" }] : []),
            ...workspaceOptions,
        ],
        [canAutoCreateWorkspace, workspaceOptions]
    );

    const workspaceDescription = canAutoCreateWorkspace
        ? "Select an existing model workspace or leave this blank to create a new one automatically."
        : "Select an existing model workspace for this dashboard.";
    const effectiveWorkspaceDescription = hasMissingLinkedWorkspace
        ? "This dashboard is linked to a missing model workspace. Select a new workspace to repair it."
        : isWorkspaceLocked
            ? "This dashboard is already linked to a model workspace and cannot be changed."
            : workspaceDescription;

    const canSubmit = String(dashCreationObject?.name ?? "").trim() !== "" &&
        (!!String(dashCreationObject?.dataModelWorkspaceId ?? "").trim() || canAutoCreateWorkspace);

    const loadWorkspaceOptions = useCallback(async () => {
        setIsLoadingWorkspaces(true);

        try {
            const result = await workspaceMgr?.Query?.({
                model: "x => x.IsActive == true && x.IsDeleted == false",
            });
            let nextWorkspaceOptions = normalizeWorkspaceOptions(result?.Items);

            if (linkedWorkspaceId && !hasWorkspaceOption(nextWorkspaceOptions, linkedWorkspaceId)) {
                const currentWorkspaceResult = await workspaceService?.single?.(linkedWorkspaceId);
                const normalizedCurrentWorkspaceResult = unwrapServiceResult(currentWorkspaceResult);
                const currentWorkspaceItem = normalizedCurrentWorkspaceResult?.Items?.[0]
                    ?? normalizedCurrentWorkspaceResult?.Item
                    ?? null;

                if (currentWorkspaceItem) {
                    nextWorkspaceOptions = normalizeWorkspaceOptions([
                        currentWorkspaceItem,
                        ...(Array.isArray(result?.Items) ? result.Items : []),
                    ]);
                }
            }

            setWorkspaceOptions(nextWorkspaceOptions);
        } finally {
            setIsLoadingWorkspaces(false);
        }
    }, [linkedWorkspaceId, workspaceMgr, workspaceService]);

    useEffect(() => {
        if (!isActive) {
            return;
        }

        void loadWorkspaceOptions();
    }, [isActive, loadWorkspaceOptions]);

    useEffect(() => {
        if (!isActive) {
            return;
        }

        setDashCreationObject(dashboard ? createDashboardDraftFromItem(dashboard) : createBlankDashboardDraft());
        setError("");
    }, [dashboard, isActive]);

    useEffect(() => {
        if (!isActive || !isEditMode || isLoadingWorkspaces) {
            return;
        }

        const currentWorkspaceId = String(dashCreationObject?.dataModelWorkspaceId ?? "").trim();
        if (!currentWorkspaceId) {
            return;
        }

        if (hasWorkspaceOption(workspaceOptions, currentWorkspaceId)) {
            return;
        }

        setDashCreationObject((prev) => ({
            ...prev,
            dataModelWorkspaceId: "",
        }));
    }, [dashCreationObject?.dataModelWorkspaceId, isActive, isEditMode, isLoadingWorkspaces, workspaceOptions]);

    const handleSave = useCallback(async () => {
        if (hasAndShowValidationErrors("dashboard")) {
            return;
        }

        setIsCreatingDashboard(true);
        setError("");

        try {
            const saveHandler = onSave ?? createNewDashboard;
            const status = await saveHandler(dashCreationObject, dashboard);
            if (status?.Success) {
                closeDialog();
                clearDashCreationObject();
                setError("");
                return;
            }

            const validationMessages = getValidationDisplayMessages(status);
            setError(
                validationMessages[0] ??
                status?.DisplayMessage ??
                status?.Message?.DisplayMessage ??
                `Unable to ${isEditMode ? "save" : "create"} dashboard.`
            );
        } catch (status) {
            const validationMessages = getValidationDisplayMessages(status);
            setError(
                validationMessages[0] ??
                status?.DisplayMessage ??
                status?.Message?.DisplayMessage ??
                `Unable to ${isEditMode ? "save" : "create"} dashboard.`
            );
        } finally {
            setIsCreatingDashboard(false);
        }
    }, [closeDialog, createNewDashboard, dashCreationObject, dashboard, hasAndShowValidationErrors, isEditMode, onSave]);

    return (
        <DialogControl
            className={"dashboard-dialog"}
            openDialog={isActive}
            disableOk={!canSubmit || isCreatingDashboard || isLoadingWorkspaces}
            title={dialogTitle}
            onOk={handleSave}
            onCancel={() => {
                closeDialog();
                clearDashCreationObject();
                setError("");
            }}
            okText={dialogOkText}
        >
            <div className="dialog-text">
                <FieldControl
                    key={`dashboard-workspace-${String(dashCreationObject?.dataModelWorkspaceId ?? "").trim()}-${modelWorkspaceOptions.length}`}
                    fieldMetadata={{
                        FieldType: SystemTypes.UIFieldType.SearchSelectList,
                        FieldName: "DataModelWorkspaceId",
                        DisplayName: "Model Workspace",
                        Description: effectiveWorkspaceDescription,
                        DataJson: modelWorkspaceOptions,
                        IsDisabled: isWorkspaceLocked,
                    }}
                    groupId={"dashboard"}
                    value={dashCreationObject?.dataModelWorkspaceId}
                    onChange={v => {
                        setDashCreationObject(prev => ({
                            ...prev,
                            dataModelWorkspaceId: normalizeSelectionValue(v),
                        }));
                    }}
                />
                {hasMissingLinkedWorkspace ? (
                    <div className="dialog-text" style={{ color: "var(--error)" }}>
                        The previously linked model workspace was not found. Pick a new workspace or leave the field blank to create one automatically.
                    </div>
                ) : null}
                <div className="dashboard-dialog-name-row">
                    <div className="dashboard-dialog-name-icon">
                        <FieldControl
                            fieldMetadata={{
                                FieldType: SystemTypes.UIFieldType.IconSelect,
                                FieldName: "DashboardIcon",
                                DisplayName: "Icon",
                                DataJson: iconSelectData,
                                DefaultSelection: "dashboard"
                            }}
                            groupId={"dashboard"}
                            value={dashCreationObject?.icon}
                            onChange={v => {
                                setDashCreationObject(prev => ({
                                    ...prev,
                                    icon: v,
                                }));
                            }}
                        />
                    </div>
                    <div className="dashboard-dialog-name-field">
                        <FieldControl
                            fieldMetadata={{
                                FieldType: SystemTypes.UIFieldType.TextBox,
                                IsRequired: true,
                                FieldName: "DashboardName",
                                DisplayName: "Dashboard Name"
                            }}
                            groupId={"dashboard"}
                            value={dashCreationObject?.name}
                            onChange={v => {
                                setDashCreationObject(prev => ({
                                    ...prev,
                                    name: v,
                                }));
                            }}
                        />
                    </div>
                </div>
                <FieldControl
                    fieldMetadata={{
                        FieldType: SystemTypes.UIFieldType.TextArea,
                        FieldName: "DashboardDescription",
                        DisplayName: "Dashboard Description"
                    }}
                    groupId={"dashboard"}
                    value={dashCreationObject?.description}
                    onChange={v => {
                        setDashCreationObject(prev => ({
                            ...prev,
                            description: v,
                        }));
                    }}
                />
              
                {canCreate("DashboardTemplateCreator") && (
                    <div className="checkbox">
                        <CheckboxControl value={dashCreationObject.isTemplate}
                            onChange={() => {
                                setDashCreationObject(prev => ({
                                    ...prev,
                                    isTemplate: !prev.isTemplate,
                                }));
                            }}
                        />
                        Mark as Template
                    </div>
                )}
                {error ? <div className="dialog-text" style={{ color: "var(--error)" }}>{error}</div> : null}
            </div>
        </DialogControl>
    )
}

export default DashboardCreateDialog
